import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Loader2, Send, FileSignature, Eye, EyeOff, Pencil } from 'lucide-react';
import { splitFullName } from '../lib/nameUtils';

const EMPTY_FIELDS = {
  company_name: '', street: '', zip_city: '',
  register_court: '', register_no: '',
  signer_name: '', signer_function: '', signer_place: '',
};

const BUTTON_MARKER = '[Vereinbarung ansehen & unterzeichnen]';

const buildEmailText = (formality, recipientName, company, senderName, opts = {}) => {
  const { first, last } = splitFullName(recipientName);
  const firstName = first || last || 'XXX';
  const lastName = last || 'XXX';
  const comp = (company || '').trim() || 'XXX';
  if (opts.broker) {
    const mandate = (opts.mandate || '').trim() || 'XXX';
    if (formality === 'du') {
      return `Hallo ${firstName},

vielen Dank für den guten Austausch!

Wie besprochen erhältst du anbei unsere Vertraulichkeitsvereinbarung (beidseitige Geheimhaltungsvereinbarung) für den weiteren Austausch zu deinem Mandat "${mandate}" zwischen der OLTA Nachfolge GmbH und der "${comp}".

Du kannst die Vereinbarung direkt online einsehen, die noch offenen Angaben ergänzen und elektronisch unterzeichnen:

${BUTTON_MARKER}

Nach deiner Unterzeichnung erhältst du die von beiden Parteien unterzeichnete Ausfertigung automatisch als PDF per E-Mail. OLTA hat die Vereinbarung bereits elektronisch gezeichnet.

Liebe Grüße,
${senderName}`;
    }
    return `Sehr geehrter Herr / Sehr geehrte Frau ${lastName},

vielen Dank für den guten Austausch!

Wie besprochen erhalten Sie anbei unsere Vertraulichkeitsvereinbarung (beidseitige Geheimhaltungsvereinbarung) für den weiteren Austausch zu Ihrem Mandat "${mandate}" zwischen der OLTA Nachfolge GmbH und der "${comp}".

Sie können die Vereinbarung direkt online einsehen, die noch offenen Angaben ergänzen und elektronisch unterzeichnen:

${BUTTON_MARKER}

Nach Ihrer Unterzeichnung erhalten Sie die von beiden Parteien unterzeichnete Ausfertigung automatisch als PDF per E-Mail. OLTA hat die Vereinbarung bereits elektronisch gezeichnet.

Mit freundlichen Grüßen
${senderName}`;
  }
  if (formality === 'du') {
    return `Hallo ${firstName},

es freut mich, dich auf diesem Wege kennenzulernen!

Wie mit Max besprochen erhältst du anbei unsere Vertraulichkeitsvereinbarung (beidseitige Geheimhaltungsvereinbarung) für die weiteren Gespräche zwischen der OLTA Nachfolge GmbH und der "${comp}".

Du kannst die Vereinbarung direkt online einsehen, die noch offenen Angaben ergänzen und elektronisch unterzeichnen:

${BUTTON_MARKER}

Nach deiner Unterzeichnung erhältst du die von beiden Parteien unterzeichnete Ausfertigung automatisch als PDF per E-Mail. OLTA hat die Vereinbarung bereits elektronisch gezeichnet.

Liebe Grüße,
${senderName}`;
  }
  return `Hallo Herr / Frau ${lastName},

es freut mich, Sie auf diesem Wege kennenzulernen!

Wie mit Max besprochen erhalten Sie anbei unsere Vertraulichkeitsvereinbarung (beidseitige Geheimhaltungsvereinbarung) für die weiteren Gespräche zwischen der OLTA Nachfolge GmbH und der "${comp}".

Sie können die Vereinbarung direkt online einsehen, die noch offenen Angaben ergänzen und elektronisch unterzeichnen:

${BUTTON_MARKER}

Nach Ihrer Unterzeichnung erhalten Sie die von beiden Parteien unterzeichnete Ausfertigung automatisch als PDF per E-Mail. OLTA hat die Vereinbarung bereits elektronisch gezeichnet.

Liebe Grüße,
${senderName}`;
};

const escHtml = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const NDA_BUTTON_PREVIEW_HTML =
  '<p><a style="display:inline-block;padding:10px 18px;background:#0f766e;color:#ffffff;text-decoration:none;border-radius:4px;font-weight:bold">Vereinbarung ansehen &amp; unterzeichnen</a></p>' +
  '<p style="font-size:12px;color:#888888">Falls der Button nicht funktioniert: <span style="color:#2563eb;text-decoration:underline">https://…/nda/… (Link wird beim Versand erzeugt)</span></p>';

const NDA_SIGNATURE_PREVIEW_HTML =
  '<p style="margin-top:18px"><b>OLTA Nachfolge GmbH</b><br>Annastraße 58-60, 52062 Aachen, Germany</p>' +
  '<p style="margin-top:10px">+49 241 5570 3830</p>' +
  '<p style="margin-top:10px"><span style="color:#2563eb;text-decoration:underline">www.olta-nachfolge.com</span></p>' +
  '<p style="margin-top:18px;color:#888888;font-size:11px">----<br>' +
  'This message and its contents are confidential, may be legally privileged, and are intended only for the use ' +
  'of the named recipient. If you are not the intended recipient, please destroy it and all its attachments, ' +
  'if any, from your system and notify the sender immediately. Any disclosure, use, copying or distribution, ' +
  'either in whole or in part, of this message is prohibited. Thank you for your cooperation.</p>';

const renderEmailPreviewHtml = (text) => {
  const parts = [];
  let buttonPlaced = false;
  (text || '').trim().split(/\n\s*\n/).forEach(para => {
    if (para.includes(BUTTON_MARKER)) {
      const [before, after] = [para.slice(0, para.indexOf(BUTTON_MARKER)), para.slice(para.indexOf(BUTTON_MARKER) + BUTTON_MARKER.length)];
      if (before.trim()) parts.push('<p>' + escHtml(before.trim()).replace(/\n/g, '<br>') + '</p>');
      parts.push(NDA_BUTTON_PREVIEW_HTML);
      if (after.trim()) parts.push('<p>' + escHtml(after.trim()).replace(/\n/g, '<br>') + '</p>');
      buttonPlaced = true;
    } else if (para.trim()) {
      parts.push('<p>' + escHtml(para).replace(/\n/g, '<br>') + '</p>');
    }
  });
  if (!buttonPlaced) parts.push(NDA_BUTTON_PREVIEW_HTML);
  return parts.join('') + NDA_SIGNATURE_PREVIEW_HTML;
};

export function SendNdaDialog({ open, onOpenChange, projectId, project, onSent }) {
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientContactId, setRecipientContactId] = useState('');
  const [fields, setFields] = useState({ ...EMPTY_FIELDS });
  const [formality, setFormality] = useState('sie');
  const [emailBody, setEmailBody] = useState('');
  const [bodyTouched, setBodyTouched] = useState(false);
  const [emailPreview, setEmailPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const senderName = user?.name?.toLowerCase().includes('talu')
    ? 'Talu Hakverdi, CFA' : (user?.name || '');
  const isBrokered = project?.deal_type === 'brokered';
  const emailOpts = { broker: isBrokered, mandate: project?.name };

  useEffect(() => {
    if (!open) return;
    setBodyTouched(false);
    api.getNdaTemplate(isBrokered ? 'broker' : 'owner').then(r => setTemplate(r.data)).catch(() => {});
    if (!isBrokered) setFields(f => ({ ...f, company_name: f.company_name || project?.name || '' }));
    Promise.all([
      api.getDealContacts(projectId).catch(() => null),
      api.getProjectContacts(projectId).catch(() => null),
    ]).then(([dc, gc]) => {
      const opts = [];
      (dc?.data?.contacts || []).forEach(c => (c.emails || []).forEach(e =>
        opts.push({ id: c.id, name: c.name, email: e, tag: c.role || 'deal contact' })));
      (gc?.data?.contacts || []).forEach(c => {
        if (c.email) opts.push({ id: c.id, name: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company, email: c.email, tag: c.type || 'contact' });
      });
      const seen = new Set();
      const deduped = opts.filter(o => o.email && !seen.has(o.email) && seen.add(o.email));
      setContacts(deduped);
      if (isBrokered) {
        const b = deduped.find(o => (o.tag || '').toLowerCase().includes('broker'));
        if (b) {
          setRecipientContactId(`${b.id}|${b.email}`);
          setRecipientName(n => n || b.name);
          setRecipientEmail(em => em || b.email);
          setFields(f => ({ ...f, signer_name: f.signer_name || b.name }));
        }
      }
    });
  }, [open, projectId, project, isBrokered]);

  useEffect(() => {
    if (!open || bodyTouched) return;
    setEmailBody(buildEmailText(formality, recipientName, fields.company_name, senderName, emailOpts));
  }, [open, bodyTouched, formality, recipientName, fields.company_name, senderName]);  // eslint-disable-line react-hooks/exhaustive-deps

  const switchFormality = (f) => {
    setFormality(f);
    setBodyTouched(false);
    setEmailBody(buildEmailText(f, recipientName, fields.company_name, senderName, emailOpts));
  };

  const pickContact = (val) => {
    setRecipientContactId(val);
    const c = contacts.find(x => `${x.id}|${x.email}` === val);
    if (c) {
      setRecipientName(c.name);
      setRecipientEmail(c.email);
      setFields(f => ({ ...f, signer_name: f.signer_name || c.name }));
    }
  };

  const setField = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const send = async () => {
    if (!recipientName.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipientEmail.trim())) {
      toast.error('Recipient name and a valid email are required'); return;
    }
    if (!fields.company_name.trim()) { toast.error('Company name is required'); return; }
    setSending(true);
    try {
      await api.sendNda(projectId, {
        recipient: { name: recipientName.trim(), email: recipientEmail.trim(), contact_id: recipientContactId.split('|')[0] || '' },
        fields, email_body: emailBody.trim(), origin: window.location.origin,
        variant: isBrokered ? 'broker' : 'owner',
      });
      toast.success(`NDA sent to ${recipientEmail.trim()} — tracking active`);
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send NDA');
    }
    setSending(false);
  };

  const fieldLabel = (k) => template?.fields?.find(f => f.key === k)?.label || k;
  const hl = (v) => v
    ? <span className="bg-teal-500/20 text-teal-200 px-1 rounded">{v}</span>
    : <span className="bg-amber-500/20 text-amber-300 px-1 rounded">[vom Empfänger auszufüllen]</span>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="send-nda-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-foreground flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-teal-400" /> Send NDA for e-signature
            {isBrokered && (
              <span data-testid="nda-broker-variant-badge"
                className="text-[9px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded px-1.5 py-0.5 uppercase">
                Broker-Variante
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            {isBrokered
              ? <>OLTA pre-signs digitally on sending. Die Vereinbarung läuft zwischen OLTA und der <b>Beratungsgesellschaft des Brokers</b> (Präambel bezieht sich auf das vermittelte Zielunternehmen). Der Broker ergänzt die markierten Felder online, unterschreibt mit getipptem Namen (Textform, § 126b BGB) und erhält die Ausfertigung automatisch als PDF — gespeichert in den Deal-Dokumenten.</>
              : <>OLTA pre-signs digitally on sending. The owner completes the highlighted fields online, signs with typed
                name + consent (Textform, § 126b BGB) and automatically receives the executed PDF — which is also saved
                to this deal's documents as <span className="text-foreground font-mono">NDA_OLTA_[Company].pdf</span>.</>}
          </p>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Recipient *</label>
            {contacts.length > 0 && (
              <select value={recipientContactId} onChange={e => pickContact(e.target.value)} data-testid="nda-recipient-select"
                className="w-full text-sm bg-secondary border border-border text-foreground rounded-sm px-2 h-9 mb-2 cursor-pointer focus:outline-none">
                <option value="">— pick a deal contact —</option>
                {contacts.map(c => (
                  <option key={`${c.id}|${c.email}`} value={`${c.id}|${c.email}`}>{c.name} · {c.email} ({c.tag})</option>
                ))}
              </select>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Input value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="Name"
                data-testid="nda-recipient-name" className="bg-secondary border-border text-foreground rounded-sm h-9" />
              <Input value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="E-Mail"
                data-testid="nda-recipient-email" className="bg-secondary border-border text-foreground rounded-sm h-9" />
            </div>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1.5">
              Contract fields — leave blank what the owner should fill in (clearly highlighted for them):
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(EMPTY_FIELDS).map(k => (
                <div key={k}>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">{fieldLabel(k)}</label>
                  <Input value={fields[k]} onChange={e => setField(k, e.target.value)}
                    data-testid={`nda-field-${k}`}
                    className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-muted-foreground">E-Mail-Text</label>
              <div className="flex items-center gap-2">
                <button onClick={() => setEmailPreview(p => !p)} data-testid="nda-email-preview-toggle"
                  className={`flex items-center gap-1 text-[11px] rounded-sm border px-2 py-1 transition-colors ${emailPreview
                    ? 'border-sky-500/50 bg-sky-500/10 text-sky-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  {emailPreview ? <><Pencil className="w-3 h-3" /> Bearbeiten</> : <><Eye className="w-3 h-3" /> Vorschau</>}
                </button>
                <div className="flex items-center gap-1 border border-border rounded-sm p-0.5">
                  <button onClick={() => switchFormality('sie')} data-testid="nda-formality-sie"
                    className={`text-[11px] px-2.5 py-1 rounded-sm transition-colors ${formality === 'sie'
                      ? 'bg-teal-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>Sie</button>
                  <button onClick={() => switchFormality('du')} data-testid="nda-formality-du"
                    className={`text-[11px] px-2.5 py-1 rounded-sm transition-colors ${formality === 'du'
                      ? 'bg-teal-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>Du</button>
                </div>
              </div>
            </div>
            {emailPreview ? (
              <div data-testid="nda-email-preview" className="w-0 min-w-full">
                <p className="text-[10px] text-muted-foreground mb-1.5">So sieht der Empfänger die E-Mail — inkl. Button und Signatur.</p>
                <div className="bg-white text-zinc-900 rounded-sm border border-border p-4">
                  <div className="text-[11px] text-zinc-500 border-b border-zinc-200 pb-2 mb-3 space-y-0.5 break-words">
                    <p><span className="font-medium text-zinc-700">To:</span> {recipientName || '—'} &lt;{recipientEmail || '—'}&gt;</p>
                    <p><span className="font-medium text-zinc-700">Subject:</span> Vertraulichkeitsvereinbarung — OLTA Nachfolge GmbH</p>
                  </div>
                  <div className="text-[13px] leading-relaxed break-words [&_p]:my-1.5"
                    dangerouslySetInnerHTML={{ __html: renderEmailPreviewHtml(emailBody) }} />
                </div>
              </div>
            ) : (
              <>
                <Textarea value={emailBody} onChange={e => { setEmailBody(e.target.value); setBodyTouched(true); }}
                  data-testid="nda-email-body" rows={14}
                  className="bg-secondary border-border text-foreground rounded-sm text-xs leading-relaxed min-h-[260px]" />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Der Unterschreiben-Button wird an der Stelle von <span className="font-mono text-foreground/70">{BUTTON_MARKER}</span> eingefügt.
                  Ihre OLTA-Signatur (Adresse, Telefon, Website, Vertraulichkeitshinweis) wird automatisch angehängt.
                </p>
              </>
            )}
          </div>

          <button onClick={() => setShowPreview(p => !p)} data-testid="nda-preview-toggle"
            className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1.5">
            {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showPreview ? 'Hide template preview' : 'Review template'}
          </button>

          {showPreview && template && (
            <div className="border border-border rounded-sm bg-secondary/20 p-4 max-h-72 overflow-y-auto text-xs text-foreground/85 space-y-2" data-testid="nda-preview">
              <p className="text-center font-bold text-sm">{template.title}</p>
              <p className="text-center text-muted-foreground">{template.subtitle}</p>
              <p className="text-center">zwischen</p>
              {template.olta_block.map((l, i) => <p key={i} className="text-center">{l}</p>)}
              <p className="text-center text-muted-foreground">– nachfolgend „OLTA“ –</p>
              <p className="text-center">und</p>
              <p className="text-center font-bold">{hl(fields.company_name)}</p>
              <p className="text-center">{hl(fields.street)}, {hl(fields.zip_city)}</p>
              <p className="text-center">eingetragen im Handelsregister des Amtsgerichts {hl(fields.register_court)} unter HRB {hl(fields.register_no)}</p>
              <p className="text-center text-muted-foreground">– nachfolgend „{template.party_label || 'Unternehmen'}“ –</p>
              <p className="font-bold mt-2">Präambel</p>
              <p>{template.preamble}</p>
              {template.sections.map((s, i) => (
                <div key={i}>
                  <p className="font-bold mt-2">{s.heading}</p>
                  {s.paragraphs.map((p, j) => <p key={j} className="mt-1">{p}</p>)}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-border">
                <div>
                  <p className="font-bold">OLTA Nachfolge GmbH</p>
                  <p className="italic text-teal-300 mt-2">wird beim Versand digital vorsigniert</p>
                </div>
                <div>
                  <p className="font-bold">{fields.company_name || (isBrokered ? '[Berater]' : '[Unternehmen]')}</p>
                  <p className="mt-2">Name: {hl(fields.signer_name)}</p>
                  <p>Funktion: {hl(fields.signer_function)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}
              className="border-border text-muted-foreground rounded-sm text-xs h-9">Cancel</Button>
            <Button onClick={send} disabled={sending} data-testid="nda-send-btn"
              className="bg-teal-600 hover:bg-teal-500 text-white rounded-sm text-xs h-9 px-4">
              {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Send NDA
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
